import { createSlice, current, type PayloadAction } from "@reduxjs/toolkit";
import { createAppThunk, type IRes } from "store/modules/common";
import Http from "commons/Http";
import {
  TranslateEditStatus,
  type IResTranslateGridList,
  type ITranslateAdminListVo,
  type ITranslateEditVo,
  type ITranslationWorkerVo,
  type TranslateTabType,
  type IContentPPChannelLisCategoryVo,
  type IResDistributionChannelListVo,
  type IDistributionListVo,
  type ISearchCookieData,
  type ITranslationTitleSelectVo,
} from "./TranslationVo";
import type { IPageInfo } from "pages/notice/store/NoticeVo";
import { Grade } from "store/modules/auth/authVo";

const name = "translate";

export interface ITranslate {
  activeTab: TranslateTabType;

  list: Array<ITranslateAdminListVo>;
  workerList?: Array<ITranslationWorkerVo>;
  titleSelectList?: Array<ITranslationTitleSelectVo>;
  workerGradeSelectList?: { id: string; name: string; value: number }[];

  page_info: IPageInfo;
  isLoading: boolean;
  edit?: {
    id: number;
    info?: ITranslateEditVo;
    status: TranslateEditStatus;
    work_content?: string;
  };
  distribution?: {
    list: IDistributionListVo[];
    selected?: IContentPPChannelLisCategoryVo[];
    authorList: { id: number; name: string }[];
  };
  cookieData?: ISearchCookieData;
}

/**
 *  번역 리스트 조회
 */
export const apiGTranslateGridListData = createAppThunk<IResTranslateGridList, { page?: number }>(
  `${name}/apiGTranslateGridListData`,
  async (param, thunkApi) => {
    const tab = thunkApi.getState().translate.cookieData
      ? thunkApi.getState().translate.cookieData?.tab
      : thunkApi.getState().translate.activeTab;
    const role = thunkApi.getState().auth.role;
    const searchText = thunkApi.getState().ui?.inputText?.[`translate_${tab}_search_text`];
    const params = {
      listType: tab,
      page:
        param.page !== undefined
          ? param.page
          : thunkApi.getState().translate.cookieData
          ? thunkApi.getState().translate.cookieData?.page
          : thunkApi.getState().translate.page_info.page,
      size: thunkApi.getState().translate.page_info.size,
      start_date: thunkApi.getState().ui?.inputText?.[`translate_option_date_start`],
      end_date: thunkApi.getState().ui?.inputText?.[`translate_option_date_end`],
      search_type: searchText ? thunkApi.getState().ui?.selectbox?.[`translate_${tab}_search_type`] : null,
      search: searchText ? searchText : null,
    };
    const apiUrl = role !== Grade.USER ? "translate/inspector/list" : "translate/worker/list";
    const res = await Http.get<IRes<IResTranslateGridList>>(apiUrl, {
      params:
        role !== Grade.USER
          ? {
              ...params,
              language: thunkApi.getState().ui?.selectbox?.[`translate_list_language`],
              job_status: thunkApi.getState().ui?.selectbox?.[`translate_job_status`]
                ? thunkApi.getState().ui?.selectbox?.[`translate_job_status`]
                : null,
            }
          : params,
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetInspectorGridlList(res.data.content.list));
      thunkApi.dispatch(
        rdxSetListPageInfo({
          page: res.data.content.page_info.page,
          size: res.data.content.page_info.size,
          total_elements: res.data.content.page_info.total_elements,
          totalPage: Math.ceil(res.data.content.page_info.total_elements! / res.data.content.page_info.size!),
        }),
      );
    }

    return res.data;
  },
);

/**
 * 콘텐츠 상세보기
 */
export const apiGTranslateEditDetail = createAppThunk<ITranslateEditVo>(
  `${name}/apiGTranslateEditDetail`,
  async (_, thunkApi) => {
    const role = thunkApi.getState().auth.role;
    const tab = thunkApi.getState().translate.activeTab;
    const id = thunkApi.getState().translate.edit?.id;

    const res = await Http.get<IRes<ITranslateEditVo>>("translate/editor", {
      params: {
        translate_assign_job_id: id,
      },
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetEditDetailInfo(res.data.content));

      if (thunkApi.getState().translate.edit?.status === TranslateEditStatus.CHECK) {
        const status =
          tab === "LIST"
            ? TranslateEditStatus.CHECK
            : role === Grade.USER
            ? TranslateEditStatus.RESULT
            : TranslateEditStatus.DIFF;
        thunkApi.dispatch(rdxSetEditStatus(status));
      }
    }

    return res.data;
  },
);

/**
 * 작업자, 감수자 => 작업 시작 또는 감수시작
 * @param job_status WORKER_TRANSLATING: 작업 시작(작업자)
 * @param job_status INSPECTOR_TRANSLATING: 감수 시작(감수자)
 */
export const apiGTranslateEditWorkStart = createAppThunk(`${name}/apiGTranslateEditWorkStart`, async (_, thunkApi) => {
  const id = thunkApi.getState().translate.edit?.id;
  const role = thunkApi.getState().auth.role;

  const res = await Http.post("translate/status/update", {
    translate_assign_job_id: id,
    job_status: role === Grade.USER ? "WORKER_TRANSLATING" : "INSPECTOR_TRANSLATING",
  });

  if (res.data) {
    thunkApi.dispatch(rdxSetEditStatus(TranslateEditStatus.EDIT));
  }
  return res.data;
});
/**
 * 프리랜서(작업자) -> 일감 저장 / 감수 요청
 * @param type SAVE: 일감 저장 / INSPECTOR: 감수요청
 */
export const apiGTranslateEditWorkerSave = createAppThunk<undefined, TranslateEditStatus>(
  `${name}/apiGTranslateEditWorkerSave`,
  async (type, thunkApi) => {
    const id = thunkApi.getState().translate.edit?.id;
    const content = thunkApi.getState().translate.edit?.work_content;
    // const content = thunkApi.getState().translate.edit?.work_content;

    const res = await Http.post("translate/worker/save", {
      translate_assign_job_id: id,
      save_type: type,
      message: thunkApi.getState().ui.inputText?.["translation_edit_memo"]
        ? thunkApi.getState().ui.inputText?.["translation_edit_memo"]
        : null,
      assign_content: content
        ? content
        : thunkApi.getState().translate.edit?.info?.worker_contents
        ? thunkApi.getState().translate.edit?.info?.worker_contents
        : thunkApi.getState().translate.edit?.info?.system_contents,
      assign_title: thunkApi.getState().ui.inputText?.["translation_inspector_title"]
        ? thunkApi.getState().ui.inputText?.["translation_inspector_title"]
        : thunkApi.getState().translate.edit?.info?.system_contents_title,
    });

    if (res.data) {
      thunkApi.dispatch(rdxSetEditStatus(type));
      if (content) {
        thunkApi.dispatch(rdxSetEditResponseContentSave({ type: "worker", content: content }));
      }
    }
    return res.data;
  },
);

/**
 * 감수자(관리자) -> 감수 저장
 */
export const apiGTranslateEditInspectorSave = createAppThunk<undefined, TranslateEditStatus>(
  `${name}/apiGTranslateEditInspectorSave`,
  async (type, thunkApi) => {
    const id = thunkApi.getState().translate.edit?.id;
    const content = thunkApi.getState().translate.edit?.work_content;

    const param = {
      translate_assign_job_id: id,
      save_type: type,
      message: thunkApi.getState().ui.inputText?.["translation_edit_memo"]
        ? thunkApi.getState().ui.inputText?.["translation_edit_memo"]
        : null,
    };
    const res = await Http.post(
      "translate/inspector/save",
      type === "REJECT"
        ? param
        : {
            ...param,
            assign_content: content
              ? content
              : thunkApi.getState().translate.edit?.info?.inspector_contents
              ? thunkApi.getState().translate.edit?.info?.inspector_contents
              : thunkApi.getState().translate.edit?.info?.worker_contents,
            assign_title: thunkApi.getState().ui.inputText?.["translation_inspector_title"]
              ? thunkApi.getState().ui.inputText?.["translation_inspector_title"]
              : thunkApi.getState().translate.edit?.info?.inspector_title
              ? thunkApi.getState().translate.edit?.info?.inspector_title
              : thunkApi.getState().translate.edit?.info?.system_contents_title,
          },
    );

    if (res.data) {
      if (type !== TranslateEditStatus.DISTRIBUTION) {
        thunkApi.dispatch(rdxSetEditStatus(type));
      }
      if (content) {
        thunkApi.dispatch(
          rdxSetEditResponseContentSave({
            type: "inspector",
            content: content,
          }),
        );
      }
    }
    return res.data;
  },
);

/**
 * 감수자(관리자) -> 배포 -> 최종 배포
 */
export const apiGTranslateEditInspectorDistribution = createAppThunk(
  `${name}/apiGTranslateEditInspectorDistribution`,
  async (_, thunkApi) => {
    const id = thunkApi.getState().translate.edit?.id;
    const selectedList = thunkApi.getState().translate.distribution?.selected;
    const status = thunkApi.getState().ui?.selectbox?.[`distribute_type`];

    const data = thunkApi.getState().ui.inputText?.["futureSubmit"];
    const time = thunkApi.getState().ui.inputText?.["futureTime"];
    const channelInfo = selectedList?.map(i => {
      return {
        distribution_channel_id: i.distribution_channel_id,
        distribution_channel_category_id: i.distribution_channel_category_id,
        author_id: thunkApi.getState().ui.selectbox?.[`translate_${i.distribution_channel_id}_author_select`]
          ? thunkApi.getState().ui.selectbox?.[`translate_${i.distribution_channel_id}_author_select`]
          : null,
      };
    });

    const res = await Http.post("translate/inspector/distribution", {
      translate_assign_job_id: id,
      distribution_channel_info: channelInfo,
      status: status,
      publish_date: status === "future" ? `${data} ${time}:00` : null,
    });

    if (res.data) {
      thunkApi.dispatch(rdxSetEditStatus(TranslateEditStatus.DISTRIBUTION));
    }
    return res.data;
  },
);

/**
 *  컨텐츠 삭제
 */
export const apiPTranslateDelete = createAppThunk<undefined, number[]>(
  `${name}/apiPTranslateDelete`,
  async (param, thunkApi) => {
    const res = await Http.post<IRes<undefined>>("translate/inspector/delete", {
      translate_assign_job_id: param,
    });
    if (res.data) {
      thunkApi.dispatch(rdxSetInspectorGridlDelete(param));
    }

    return res.data;
  },
);

/**
 * 배포 클릭 시 배포 팝업 > 번역 배포 채널 api
 */
export const apiGTranslateDistributeChannelList = createAppThunk<IResDistributionChannelListVo, boolean>(
  `${name}/apiGTranslateDistributeChannelList`,
  async (param, thunkApi) => {
    const id = thunkApi.getState().translate.edit?.id;
    const res = await Http.get<IRes<IResDistributionChannelListVo>>("translate/inspector/distribution/list", {
      params: {
        translate_assign_job_id: id,
        translate_language: thunkApi.getState().ui.radioBox?.["translation_language_type"]
          ? thunkApi.getState().ui.radioBox?.["translation_language_type"]
          : "EN",
        distribution_channel_name: param
          ? thunkApi.getState().ui.inputText?.["pp_list_search"]
            ? thunkApi.getState().ui.inputText?.["pp_list_search"]
            : null
          : null,
      },
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetDistributionChannelList(res.data.content));
    }

    return res.data;
  },
);

/**
 * 배포 클릭 시 배포 팝업 > 글쓴이 리스트 호출
 */
export const apiGTranslateAuthorList = createAppThunk<{ id: number; name: string }[], number>(
  `${name}/apiGTranslateAuthorList`,
  async (param, thunkApi) => {
    const res = await Http.get<IRes<{ id: number; name: string }[]>>("translate/author", {
      params: {
        distribution_channel_id: param,
      },
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetDistributionSelectedAuthorList(res.data.content));
    }

    return res.data;
  },
);

/**
 *  번역 작업자 프리랜서 리스트 조회
 */
export const apiGTranslateWorkerList = createAppThunk<ITranslationWorkerVo[]>(
  `${name}/apiGTranslateWorkerList`,
  async (_, thunkApi) => {
    const res = await Http.get<IRes<ITranslationWorkerVo[]>>("translate/inspector/worker/list");

    if (res.data.content) {
      thunkApi.dispatch(rdxSetWorkerList(res.data.content));
    }

    return res.data;
  },
);

/**
 *  번역 담당자 배정 save
 */
export const apiPTranslateWorkerSave = createAppThunk<
  undefined,
  { worker_user_id: number; translate_assign_job_id: number[] }
>(`${name}/apiPTranslateWorkerSave`, async (params, thunkApi) => {
  const res = await Http.post<IRes<undefined>>("translate/inspector/worker/list/save", params);

  // if (res.data.content) {
  //   console.log(res.data.content);
  // }

  return res.data;
});

/**
 *  컨텐츠 추천 타이틀 조회
 */
export const apiGTranslateTitleSelectList = createAppThunk<ITranslationTitleSelectVo[]>(
  `${name}/apiGTranslateTitleSelectList`,
  async (_, thunkApi) => {
    const id = thunkApi.getState().translate.edit?.id;
    const res = await Http.get<IRes<ITranslationTitleSelectVo[]>>("translate/inspector/title/list", {
      params: { translate_assign_job_id: id },
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetTitleSelectList(res.data.content));
    }

    return res.data;
  },
);

/**
 *  컨텐츠 추천 타이틀 추천 선택 시
 */
export const apiPTranslateTitleSelectSave = createAppThunk<undefined, { translate_title_id: number }>(
  `${name}/apiPTranslateTitleSelectSave`,
  async (params, thunkApi) => {
    const res = await Http.post<IRes<undefined>>("translate/inspector/title", params);

    return res.data;
  },
);

/**
 *  작업자 등급 select list 조회
 */
export const apiGTranslateWorkerGradeSelectList = createAppThunk<{ translate_grade_id: number; grade: string }[]>(
  `${name}/apiGTranslateTitleSelectList`,
  async (_, thunkApi) => {
    const res = await Http.get<IRes<{ translate_grade_id: number; grade: string }[]>>(
      "translate/inspector/grade/select-box",
    );

    if (res.data.content) {
      thunkApi.dispatch(rdxSetWorkerGradeSelectList(res.data.content));
    }

    return res.data;
  },
);

/**
 * 작업자 등급 저장
 */
export const apiPTranslateWorkerGradeSave = createAppThunk(`${name}/apiPTranslateWorkerSave`, async (_, thunkApi) => {
  const id = thunkApi.getState().translate.edit?.id;
  const grade = thunkApi.getState().ui?.selectbox?.["translate_worker_grade_select"];
  let gradeNum;
  switch (grade) {
    case "S":
      gradeNum = 1;
      break;
    case "A":
      gradeNum = 2;
      break;
    case "B":
      gradeNum = 3;
      break;
    case "C":
      gradeNum = 4;
      break;
    default:
      gradeNum = undefined;
      break;
  }

  const res = await Http.post<IRes<undefined>>("translate/inspector/grade", {
    translate_assign_job_id: id,
    translate_grade_id: gradeNum,
  });

  return res.data;
});

const translateSlice = createSlice({
  name,
  initialState: {
    activeTab: "LIST",
    list: [],
    page_info: {
      total_elements: 0,
      page: 0,
      size: 20,
      totalPage: 0,
    },
    isLoading: false,
  } as ITranslate,
  reducers: {
    rdxSetTranslateActiveTab(state: ITranslate, action: PayloadAction<TranslateTabType>) {
      state.activeTab = action.payload;
    },
    rdxSetInspectorGridlList(state: ITranslate, action: PayloadAction<ITranslateAdminListVo[]>) {
      state.list = action.payload;
    },
    rdxSetListPageInfo(state: ITranslate, action: PayloadAction<IPageInfo>) {
      state.page_info = action.payload;
    },

    rdxSetWorkerList(state: ITranslate, action: PayloadAction<ITranslationWorkerVo[]>) {
      state.workerList = action.payload;
    },

    //컨텐츠 추천 타이틀
    rdxSetTitleSelectList(state: ITranslate, action: PayloadAction<ITranslationTitleSelectVo[]>) {
      state.titleSelectList = action.payload;
    },

    //프리랜서 번역 등급 설정 select list
    rdxSetWorkerGradeSelectList(
      state: ITranslate,
      action: PayloadAction<{ translate_grade_id: number; grade: string }[]>,
    ) {
      state.workerGradeSelectList = action.payload.map(i => {
        return { id: i.grade, name: i.grade, value: i.translate_grade_id };
      });
    },

    rdxSetEditJobId(state: ITranslate, action: PayloadAction<{ id: number; status?: string }>) {
      let status;
      switch (action.payload.status) {
        case "DISTRIBUTION_SUCCESS":
          status = TranslateEditStatus.DISTRIBUTION;
          break;
        case "DISTRIBUTION_READY":
          status = TranslateEditStatus.DISTRIBUTION_READY;
          break;
        case "WORKER_READY":
          status = TranslateEditStatus.READY;
          break;
        case "INSPECTOR_REJECT":
          status = TranslateEditStatus.REJECT;
          break;
        default:
          status = TranslateEditStatus.CHECK;
          break;
      }
      state.edit = {
        id: action.payload.id,
        status: status,
      };
    },

    rdxSetEditDetailInfo(state: ITranslate, action: PayloadAction<ITranslateEditVo>) {
      state.edit!.info = action.payload;
    },

    rdxSetEditStatus(state: ITranslate, action: PayloadAction<TranslateEditStatus>) {
      state.edit!.status = action.payload;
    },

    rdxSetEditResponseContentSave(state: ITranslate, action: PayloadAction<{ type: string; content: string }>) {
      if (action.payload.type === "worker") {
        state.edit!.info!.worker_contents = action.payload.content;
      } else {
        state.edit!.info!.inspector_contents = action.payload.content;
      }
    },

    rdxSetEditWorkContentSave(state: ITranslate, action: PayloadAction<string>) {
      if (state.edit) {
        state.edit!.work_content = action.payload;
      }
    },

    rdxSetDistributionChannelList(state: ITranslate, action: PayloadAction<IResDistributionChannelListVo>) {
      const channel = action.payload.origin_distribution_channel;

      state.distribution = {
        list: action.payload.distribution_channel_projection,
        selected: [
          {
            distribution_channel_id: channel.origin_distribution_channel_id,
            distribution_channel_name: channel.origin_distribution_channel_name,
            distribution_channel_category_id: channel.origin_distribution_channel_category_id,
            distribution_channel_category_name: channel.origin_distribution_channel_category_name,
          },
        ],
        authorList: [],
      };
    },

    rdxSetDistributionSelectedChannelList(state: ITranslate, action: PayloadAction<IContentPPChannelLisCategoryVo>) {
      const selected = state.distribution?.selected;
      if (selected) {
        const ides = selected.map(i => i.distribution_channel_category_id);
        if (!ides.includes(action.payload.distribution_channel_category_id)) {
          state.distribution!.selected = [...selected, action.payload];
        }
      } else {
        state.distribution!.selected = [action.payload];
      }
    },
    rdxSetDistributionRemoveChannelList(state: ITranslate, action: PayloadAction<number>) {
      const selected = state.distribution?.selected ? state.distribution!.selected : [];
      const result = selected.filter(item => item.distribution_channel_category_id !== action.payload);
      state.distribution!.selected = result;
    },

    rdxSetDistributionSelectedAuthorList(state: ITranslate, action: PayloadAction<{ id: number; name: string }[]>) {
      const list = action.payload.map(i => {
        return { ...i, value: i.id };
      });
      state.distribution!.authorList = list;
    },

    rdxSetSearchCookieData(state: ITranslate, action: PayloadAction<ISearchCookieData>) {
      state.cookieData = action.payload;
    },

    rdxSetInspectorGridlDelete(state: ITranslate, action: PayloadAction<number[]>) {
      state.list = state.list.filter(item => !action.payload.includes(item.translate_assign_job_id));
    },
    // 리덕스 초기화
    rdxResetTranslation(state: ITranslate) {
      state.activeTab = "LIST";
      state.list = [];
      state.page_info = {
        total_elements: 0,
        page: 0,
        size: 20,
        totalPage: 0,
      };
      state.isLoading = false;
    },
    rdxResetWorkerList(state: ITranslate) {
      delete state.workerList;
    },
    rdxResetTitleSelectList(state: ITranslate) {
      delete state.titleSelectList;
    },
    rdxResetEditDetailInfo(state: ITranslate) {
      delete state.edit;
      delete state.workerGradeSelectList;
    },
    rdxResetDistributionInfo(state: ITranslate) {
      delete state.distribution;
    },
    rdxResetSearchCookieData(state: ITranslate) {
      delete state.cookieData;
    },
  },
  extraReducers: builder => {
    builder.addCase(apiGTranslateGridListData.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(apiGTranslateGridListData.fulfilled, state => {
      state.isLoading = false;
    });
  },
});

export const {
  rdxSetTranslateActiveTab,
  rdxSetInspectorGridlList,
  rdxSetListPageInfo,
  rdxSetWorkerList,
  rdxSetTitleSelectList,
  rdxSetInspectorGridlDelete,
  rdxSetWorkerGradeSelectList,

  rdxSetEditJobId,
  rdxSetEditDetailInfo,
  rdxSetEditStatus,
  rdxSetEditWorkContentSave,
  rdxSetEditResponseContentSave,
  rdxSetDistributionChannelList,
  rdxSetDistributionSelectedChannelList,
  rdxSetDistributionRemoveChannelList,
  rdxSetDistributionSelectedAuthorList,

  rdxSetSearchCookieData,

  //리덕스 초기화
  rdxResetTranslation,
  rdxResetWorkerList,
  rdxResetTitleSelectList,
  rdxResetEditDetailInfo,
  rdxResetDistributionInfo,

  rdxResetSearchCookieData,
} = translateSlice.actions;
// data 를 관리하는 reducer 기본 반환 설정
export default translateSlice.reducer;
